import React, { useState, useMemo } from "react";
import {
  Chart,
  CommonSeriesSettings,
  Series,
  ValueAxis,
  ArgumentAxis,
  Label,
  Export,
  Format,
  Grid,
  Tooltip,
  Legend,
  Size,
  Title,
} from "devextreme-react/chart";
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import { useFetch } from "src/hooks/useFetch";
import { SpatialRun } from "../types";

// ============ TypeScript Interfaces ============

interface RawCellAssignmentStats {
  MeanReadCountPerCell?: number;
  ReadCountPerMM2?: number;
  RawExtraCellularRatio?: number;
}

interface WellCellRawAssignmentStat {
  WellLocation: string;
  Failure?: {
    Code: string;
    Message: string;
  } | null;
  RawCellAssignmentStats?: RawCellAssignmentStats | null;
  TargetProjectionRegistrationStats?: {
    Target: string;
    WellProjectionRegistrationSuccessRate: number;
    TileProjectionRegistrationSuccessRates: number[];
  } | null;
}

interface BatchRawAssignmentData {
  BatchName: string;
  WellCellRawAssignmentStats?: WellCellRawAssignmentStat[];
  RunTargetProjectionRegistrationSuccessRate?: number;
}

// API returns an object with batch names as keys
type RawCellAssignmentResponse = Record<string, BatchRawAssignmentData>;

// ============ Metric Options ============

const METRIC_OPTIONS = [
  { value: "ReadCountPerMM2", label: "Read Counts Per MM²", precision: 2 },
  { value: "MeanReadCountPerCell", label: "Mean Read Count Per Cell", precision: 2 },
  { value: "RawExtraCellularRatio", label: "Raw Extra Cellular Ratio", precision: 4 },
];

// ============ Main Component ============

interface CytoPolonyAssignmentProps {
  run: SpatialRun;
}

const CytoPolonyAssignment = ({ run }: CytoPolonyAssignmentProps) => {
  const {
    data: rawData,
    isLoading,
    isError,
    error,
  } = useFetch(`/spatialRun/${run?.runID}/rawCellAssignment`, !!run?.runID);

  // State for selected metric
  const [selectedMetric, setSelectedMetric] = useState<string>("ReadCountPerMM2");

  // Parse the response - it's an object with batch names as keys
  const responseData = rawData as RawCellAssignmentResponse | undefined;

  // Get batch names from the response keys
  const batchNames = useMemo(() => {
    if (!responseData) return [];
    return Object.keys(responseData);
  }, [responseData]);

  // Get all unique well locations across all batches
  const wellLocations = useMemo(() => {
    if (!responseData) return [];
    
    const wellSet = new Set<string>();
    Object.values(responseData).forEach((batch) => {
      batch.WellCellRawAssignmentStats?.forEach((well) => {
        wellSet.add(well.WellLocation);
      });
    });
    
    // Sort wells naturally (A1, A2, B1, B2, etc.)
    return Array.from(wellSet).sort((a, b) => {
      const aMatch = a.match(/([A-Z]+)(\d+)/);
      const bMatch = b.match(/([A-Z]+)(\d+)/);
      if (aMatch && bMatch) {
        const letterCompare = aMatch[1].localeCompare(bMatch[1]);
        if (letterCompare !== 0) return letterCompare;
        return parseInt(aMatch[2]) - parseInt(bMatch[2]);
      }
      return a.localeCompare(b);
    });
  }, [responseData]);

  // Transform data for the chart: each row is a well with batch values as columns
  const chartData = useMemo(() => {
    if (!responseData || !wellLocations.length) return [];

    return wellLocations.map((wellLocation) => {
      const row: Record<string, string | number | null> = { Well: wellLocation };
      
      batchNames.forEach((batchName) => {
        const batch = responseData[batchName];
        const wellData = batch.WellCellRawAssignmentStats?.find(
          (w) => w.WellLocation === wellLocation
        );
        
        const stats = wellData?.RawCellAssignmentStats;
        const value = stats?.[selectedMetric as keyof RawCellAssignmentStats];
        
        // Use batch name as the field, handle missing data
        row[batchName] = value !== undefined && value !== null ? value : null;
      });
      
      return row;
    });
  }, [responseData, wellLocations, batchNames, selectedMetric]);

  // Get the selected metric option for formatting
  const selectedMetricOption = METRIC_OPTIONS.find(
    (option) => option.value === selectedMetric
  );

  // ============ Loading and Error States ============

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading polony assignment data...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error loading polony assignment data: {String(error)}
      </Typography>
    );
  }

  if (!responseData || Object.keys(responseData).length === 0) {
    return <Typography>No polony assignment data available for this run.</Typography>;
  }

  // ============ Render ============

  return (
    <Box
      sx={{
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
        pr: 2,
      }}
    >
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Polony Assignment (3' Untargeted)
      </Typography>

      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="metric-select-label">Select Metric</InputLabel>
          <Select
            labelId="metric-select-label"
            value={selectedMetric}
            label="Select Metric"
            onChange={(e) => setSelectedMetric(e.target.value)}
          >
            {METRIC_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Chart dataSource={chartData} style={{ marginTop: 20 }}>
        <Title
          horizontalAlignment="center"
          text={`${selectedMetricOption?.label || selectedMetric} by Well`}
        />
        <Size width="100%" />
        <CommonSeriesSettings argumentField="Well" type="bar" barPadding={0.1} />
        {batchNames.map((batchName) => (
          <Series key={batchName} valueField={batchName} name={batchName} />
        ))}
        <ValueAxis title={selectedMetricOption?.label || selectedMetric}>
          <Grid visible={true} />
        </ValueAxis>
        <ArgumentAxis title="Well">
          <Label wordWrap="none" overlappingBehavior="stagger" />
        </ArgumentAxis>
        <Legend
          verticalAlignment="top"
          horizontalAlignment="right"
        />
        <Export enabled />
        <Tooltip enabled>
          <Format type="fixedPoint" precision={selectedMetricOption?.precision || 2} />
        </Tooltip>
      </Chart>
    </Box>
  );
};

export default CytoPolonyAssignment;
